import h from 'hyperscript';

import { assert, assertUnreachable, requireNonNullable } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { groupBy } from '@disy/lib/type-util';
import { createAnchorButton, createButton } from '@disy/ui/button';
import type { Content } from '@disy/ui/content';
import { setupPopupButton } from '@disy/ui/popup';
import { getSetting } from '@disy/workbook.lib/settings';

import { showChangeExternalLayerCredentialsDialog } from 'cadenza/map/add-external-layers-wizard/show-add-external-layers-wizard';
import { getStore } from 'cadenza/store/cadenza-store';
import { createLayerLabel } from 'cadenza/ui/layer-label/layer-label';
import { createVisualizationTypeLabel } from 'cadenza/ui/visualization-type-label/visualization-type-label';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { editActiveAttribute } from 'cadenza/workbook/attribute-modification-dialog/edit-attribute';
import { findFirstAttributeWithStatus } from 'cadenza/workbook/data-view/data-view-utils';
import { showEnrichmentDependenciesDialog } from 'cadenza/workbook/enrichment/dependency/enrichment-dependencies-dialog/show-enrichment-dependencies-dialog';
import type { EnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { getLayerLabelType } from 'cadenza/workbook/map/workbook-layer-icon-utils';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import type {
  DataViewNotification,
  LayerNotification,
  NotificationAction,
  WorkbookNotification,
} from 'cadenza/workbook/notifications/workbook-notifications';
import {
  compareNotificationsBySeverity,
  isDataViewNotification,
} from 'cadenza/workbook/notifications/workbook-notifications';
import {
  getChartDataViewNameWithSuffixIfApplicable,
  hasReferenceLine,
  hasTrendLine,
} from 'cadenza/workbook/state/workbook-chart-view-selectors.state';
import { getViewTreeModel } from 'cadenza/workbook/state/workbook-layer-tree-model';
import type { CollapsedNotification } from 'cadenza/workbook/view/view-header/view-notification-bar';
import {
  getLayerData,
  getLayerName,
  getViewById,
  getVisualizationKeyByDataViewId,
  layerById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { compareDataViewsByAppearance } from 'cadenza/workbook/workbook-utils';

import './view-notification-bar-details.css';
import i18n from './view-notification-bar-details.properties';

const BASE_CSS_CLASS = 'd-view-notification-bar-details';

export function createDetailLinkElement(viewId: ViewId, collapsedNotifications: CollapsedNotification[]) {
  const button = createButton(h('span.ellipsis', i18n('view.notification.bar.details')), {
    styleClass: `${BASE_CSS_CLASS}--button`,
    size: 's',
    variant: 'link',
  });

  setupPopupButton(button, () => createNotificationsDetail(viewId, collapsedNotifications), {
    arrow: true,
    styleClass: `${BASE_CSS_CLASS}--popup`,
  });

  return button;
}

function createNotificationsDetail(viewId: ViewId, collapsedNotifications: CollapsedNotification[]) {
  const view = getViewById(getStore().getState(), viewId);
  if (view.type === 'MAP') {
    const layerTree = getViewTreeModel(getStore().getState(), viewId);
    const layerIdsInMapOrder = layerTree
      .getAllDescendants(layerTree.rootId)
      .map((layerNode) => layerNode.id)
      .reverse(); // the visual order in the list is reversed
    return createGroupedNotificationsDetail(collapsedNotifications, layerIdsInMapOrder, (layerId) =>
      createMapLayerLabel(layerId, viewId),
    );
  } else if (hasReferenceLine(getStore().getState(), viewId) || hasTrendLine(getStore().getState(), viewId)) {
    return createGroupedNotificationsDetail(
      collapsedNotifications,
      view.dataViews.toSorted((a, b) => compareDataViewsByAppearance(a, b)),
      (layerId) => createVisualizationLabel(toDataViewId(layerId)),
    );
  } else {
    return createUngroupedNotificationsDetail(collapsedNotifications);
  }
}

function createMapLayerLabel(layerId: LayerId, viewId: ViewId): HTMLElement {
  const layerName = getLayerName(getStore().getState(), viewId, layerId);
  const layerData = getLayerData(getStore().getState(), viewId, layerId);
  const type = layerData.definition.status === 'OK' ? getLayerLabelType(layerData) : 'unknown';
  return createLayerLabel(type, layerName);
}

function createVisualizationLabel(dataViewId: DataViewId) {
  return createVisualizationTypeLabel(
    getVisualizationKeyByDataViewId(getStore().getState(), dataViewId),
    getChartDataViewNameWithSuffixIfApplicable(getStore().getState(), dataViewId),
  );
}

// For non-map views we just show all the view notifications ordered by severity
function createUngroupedNotificationsDetail(collapsedNotifications: CollapsedNotification[]): Content {
  return collapsedNotifications.sort(compareNotificationsBySeverity).map(createNotificationAlertElement);
}

// For maps and charts with reference lines, we group notifications by layer/dataView, showing the layer/dataView name as a header for each group.
// We order the layer/dataView in the same order as in the layer list in designer.
// Within each group, notifications are ordered by severity (errors before warnings)
function createGroupedNotificationsDetail(
  collapsedNotifications: CollapsedNotification[],
  orderedLayerIds: LayerId[],
  labelProvider: (layerId: LayerId) => HTMLElement,
) {
  const notificationsByLayerId = groupNotifications(collapsedNotifications);

  return [
    ...(notificationsByLayerId['VIEW' as LayerId]?.map(createNotificationAlertElement) ?? []),
    ...orderedLayerIds.flatMap((layerId) => {
      const notificationsInGroup = notificationsByLayerId[layerId];
      if (!notificationsInGroup) {
        return [];
      }
      return [
        h('.container', [
          h('.d-view-notification-bar-details--popup-heading', labelProvider(layerId)),
          ...notificationsInGroup.map(createNotificationAlertElement),
        ]),
      ];
    }),
  ];
}

// group notifications by layer/dataview ID, put the view-level notifications under the 'VIEW' key.
function groupNotifications(collapsedNotifications: CollapsedNotification[]) {
  const notificationsByLayerId = groupBy(
    collapsedNotifications,
    ({ notification }) =>
      (notification as LayerNotification).layerId ?? (notification as DataViewNotification).dataViewId ?? 'VIEW',
  );

  // within each group, order by severity
  Object.keys(notificationsByLayerId).forEach((key) => {
    notificationsByLayerId[key as LayerId].sort(compareNotificationsBySeverity);
  });

  return notificationsByLayerId;
}

function createNotificationAlertElement({ notification, count }: CollapsedNotification) {
  const notificationTitle =
    count > 1 ?
      `${notification.title} ${i18n('view.notification.bar.count', { countNumber: count })}`
    : notification.title;
  let alertElement = h('d-alert.d-alert-small', { type: notification.level.toLowerCase() }, [
    h('p.title', notificationTitle),
    h('d-truncated-p.message', notification.detail),
  ]);

  if (notification.action) {
    const actionButton = createAnchorButton(notification.action.label, {
      onclick: () => actionExecutor(notification),
    });

    alertElement.appendChild(actionButton);
  }

  return alertElement;
}

function actionExecutor(notification: WorkbookNotification) {
  const action = notification.action!; // we know there is an action if we call this function

  switch (action.name) {
    case 'CHANGE_WMS_LAYER_CREDENTIALS':
      executeActionChangeLayerWmsCredentials(notification);
      break;
    case 'EDIT_ACTIVE_ATTRIBUTE':
      executeActionEditActiveAttribute(notification);
      break;
    case 'REVIEW_ENRICHMENT_DEPENDENCIES':
      executeActionReviewEnrichmentDependencies(action);
      break;
    default:
      assertUnreachable(action.name);
  }
}

/**
 * Executes the "Change WMS layer credential action, which shows a dialog.
 *
 * This is very specific use-case logic and doesn't belong in this component. We should refactor the
 * action system so that action logic exists close to the related use-case code, and this component
 * remains just to show notifications.
 */
function executeActionChangeLayerWmsCredentials(notification: WorkbookNotification) {
  const layerId = (notification as LayerNotification).layerId;
  const layer = layerById(getStore().getState(), notification.viewId, layerId);
  const wmsUrl = getSetting<string>(layer, LayerSettingsKey.toPersistenceKey('EXTERNAL_LAYER_SERVICE_URL'))!;
  showChangeExternalLayerCredentialsDialog({ layerId, wmsUrl }).catch((error) =>
    logger.error('Could not change external layer credentials', error),
  );
}

/**
 * Executes the "Edit Active Attribute" action, which shows the attribute modification dialog.
 */
function executeActionEditActiveAttribute(notification: WorkbookNotification) {
  assert(isDataViewNotification(notification), 'action is only executed for DataViewNotifications');
  // Since we don't have the id of the attribute in question, get the first attribute on the
  // dataView with an incompatible aggregation function.
  const attributeId = findFirstAttributeWithStatus(notification.dataViewId, [
    'INCOMPATIBLE_AGGREGATION_FUNCTION',
    'INCOMPATIBLE_AGGREGATIONS_FOR_FRACTION_CALCULATION',
    'INCOMPATIBLE_FRACTION_CALCULATION_FOR_TABLE_MODE',
  ])!.id as ActiveAttributeId;
  if (attributeId) {
    editActiveAttribute(attributeId, notification.dataViewId);
  }
}

/**
 * Executes the "review enrichment dependencies" action, which opens the review enrichment dialog.
 */
async function executeActionReviewEnrichmentDependencies(notificationAction: NotificationAction) {
  const enrichment = requireNonNullable(notificationAction.params?.['enrichment']) as EnrichmentDto;
  return showEnrichmentDependenciesDialog(enrichment.enrichedObjecttypeId, enrichment.objecttypeId);
}
